<template>
  <div style="display: flex; flex-direction: column; width: 100%">
    <div style="display: flex; align-items: baseline; flex-wrap: nowrap">
      <div style="flex-grow: 1">
        <div>
          <div class="control is-flex is-small">
            <input
              class="input is-small"
              :class="{
                'is-danger': hasError,
                'is-success': inputStr !== '',
              }"
              :value="inputStr"
              :placeholder="placeholder"
              @input="changeInputStr"
            />
            <button
              v-if="showReMode"
              class="button is-small"
              :class="[useRegex ? 'is-primary' : 'is-outlined']"
              :disabled="!canChangeMode"
              @click="changeIsRegex(!useRegex)"
            >
              <i
                class="icon fa"
                :class="[
                  useRegex ? 'is-primary fa-toggle-on' : 'fa-toggle-off',
                ]"
              ></i>
              <span>RE</span>
            </button>
          </div>
        </div>
        <div>
          <p v-if="hasError" class="is-size-7 has-text-danger">
            {{ regexError }}
          </p>
          <p
            v-if="showFilteredCount && inputStr.length > 0"
            class="is-size-7 has-text-success"
          >
            Matching {{ filteredListOfValues.length }}
            {{ pluralize('item', filteredItemsCount) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import pluralize from 'pluralize'

class InvalidRegexError extends Error {
  constructor(message) {
    super(message)
    this.name = 'InvalidRegexError'
  }
}

export default {
  emits: ['input', 'change-is-regex', 'filter-list', 'filter-matches'],
  props: {
    initInput: {
      type: String,
      default: '',
    },
    initIsRegex: {
      type: Boolean,
      default: false,
    },
    showReMode: {
      type: Boolean,
      default: true,
    },
    canChangeMode: {
      type: Boolean,
      default: true,
    },
    caseInsensitive: {
      type: Boolean,
      default: true,
    },
    showFilteredCount: {
      type: Boolean,
      default: true,
    },
    listOfValues: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputStr: '',
      useRegex: false,
      matches: [],
      regexError: null,
    }
  },
  computed: {
    matchFunc() {
      return this.useRegex ? this.regexMatch : this.substrMatch
    },
    hasError() {
      return this.regexError !== null
    },
    regexFilt() {
      let regexFilt = null
      let flags = (this.caseInsensitive && 'i') || undefined
      if (this.useRegex) {
        try {
          regexFilt = new RegExp(this.inputStr, flags)
        } catch (error) {
          if (error instanceof SyntaxError) {
            throw new InvalidRegexError(error.message)
          }
        }
      }
      return regexFilt
    },
    triggerComputeMatch() {
      return [this.inputStr, this.listOfValues, this.matchFunc]
    },
    filteredListOfValues() {
      if (this.inputStr === '' || this.hasError) {
        /* In case of error or empty input
         * we want the original list */
        return this.listOfValues
      }
      return this.listOfValues.filter(
        (_, vIx) =>
          this.matches && this.matches[vIx] && this.matches[vIx].match,
      )
    },
    filteredListOfMatchValues() {
      return this.listOfValues.map(
        (_, vIx) =>
          (this.matches && this.matches[vIx] && this.matches[vIx].matchValue) ||
          [],
      )
    },
    filteredItemsCount() {
      return this.listOfValues.length - this.filteredListOfValues.length
    },
  },
  methods: {
    pluralize,
    initialize() {
      this.inputStr = this.initInput || ''
      this.useRegex = this.initIsRegex || false
      this.$emit('input', this.inputStr)
    },
    substrMatch(value) {
      let inputString = this.inputStr
      let val = value
      if (this.caseInsensitive) {
        inputString = inputString.toLowerCase()
        val = val.toLowerCase()
      }
      const matchedIndex = val.indexOf(inputString)
      return {
        match: matchedIndex !== -1,
        matchValue: [this.inputStr],
      }
    },
    regexMatch(value) {
      /* regexFilt is a computed property so modifying
       * a prop of it is a bit bold. Note: If lastIndex is
       * not reset it does not behave properly when called
       * sequencially */
      let matchedValue = null
      this.regexFilt.lastIndex = 0
      matchedValue = this.regexFilt?.exec(value)
      return {
        match: matchedValue !== null,
        matchValue: matchedValue,
      }
    },
    computeMatches() {
      const matches = []
      for (var i = 0; i < this.listOfValues.length; i++) {
        const value = this.listOfValues[i]?.value
        let match = null
        match = this.matchFunc(value)
        matches.push(match)
      }
      return matches
    },
    changeInputStr(e) {
      const inputValue = e.target.value
      this.$emit('input', inputValue)
      this.inputStr = inputValue
    },
    changeIsRegex(isRegex) {
      this.$emit('change-is-regex', isRegex)
      this.useRegex = isRegex
    },
  },
  watch: {
    initInput: {
      handler: function () {
        this.initialize()
      },
      immediate: true,
    },
    triggerComputeMatch: {
      handler: function () {
        this.matches = []
        this.regexError = null
        try {
          this.matches = this.computeMatches()
        } catch (error) {
          this.regexError = error
        }
      },
      immediate: true,
    },
    filteredListOfValues: {
      handler: function (f) {
        this.$emit('filter-list', f)
      },
      immediate: true,
    },
    filteredListOfMatchValues: {
      handler: function (f) {
        this.$emit('filter-matches', f)
      },
      immediate: true,
    },
  },
  beforeMount() {
    this.initialize()
  },
}
</script>
